import { Card, CardContent } from "../../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { TrendingUp } from "lucide-react";
import { classifyCategory, xirr as computeXirr, parseDDMMYYYY, computeLotEconomics } from "./subCategoryClassifier";

const CATEGORIES = ["Equity", "Debt", "Hybrid", "Commodity", "Others"];
const emptyBucket = () => ({
  investment: 0, switchIn: 0, switchOut: 0, redemption: 0, dividendPayout: 0,
  marketValue: 0, unrealised: 0, cost: 0, cashflows: [],
});
const fmtIN = (n) => `\u20b9${Math.round(Math.abs(Number(n) || 0)).toLocaleString("en-IN")}`;
const fmtSigned = (n) => (n >= 0 ? `+${fmtIN(n)}` : `\u2212${fmtIN(n)}`);
const dash = (n) => (Math.abs(n) < 1 ? "-" : fmtIN(n));

/**
 * Section C — Cashflow Snapshot table (5-category × 13 rows).
 * Mirrors the reference system: Equity / Debt / Hybrid / Commodity / Others / Total.
 *
 * Switch In / Switch Out are DISPLAYED in the table but EXCLUDED from the
 * XIRR cashflows array — they are internal fund reallocations, not real
 * client-cash movements. XIRR is Active-Lot: per-lot cost basis as dated
 * -outflows from filteredActiveUnits + terminal +market_value at report
 * date. This matches MyPlanner (Kanwaljit Kohli verified 2026-09-08:
 * both show 12.12%).
 */
export const CashflowStatusCard = ({
  allTransactions = [],
  filteredActiveUnits = [],
  activeUnitsCostMap = {},
  holdingsFilteredByPattern = [],
}) => {
  const buckets = Object.fromEntries(CATEGORIES.map((c) => [c, emptyBucket()]));

  // Bucket every transaction row for DISPLAY totals (rows a–e).
  //
  // Classification is DESCRIPTION-DRIVEN (per user directive 2026-09-08:
  // "follow the description. Excel doesn't show the description").
  // The old txn_type-prefix rules mis-bucketed STP In as Investment
  // (because treatment=add units and tt=STPA didn't match "SI") and dropped
  // STP Out silently (tt=STPO doesn't start with "SO"). This produced a
  // ~₹7 Lakh drift vs MyPlanner for Ankur Ravi Chandak.
  //
  // XIRR rule (Active-Lot mode — reverted 2026-09-08 after LIVE
  // reconciliation of Kanwaljit Kohli showed MyPlanner uses active-lot
  // XIRR, not all-cashflow. MyPlanner XIRR = 12.12%; Kinntegra active-lot
  // XIRR = 12.12% match; simulated all-cashflow XIRR = 6.72% diverges).
  // Keep cost/market/unrealised + XIRR cashflows both sourced from
  // filteredActiveUnits below. STPs/Switches never enter XIRR by design.
  const RE_EXCLUDE = /rejection|pre\s*rejection|pledged|unpledged|dematerialized|consolidation|transmission|refund|segregated|bonus|mf\s*gift/i;
  const RE_PURCHASE = /^(purchase|sip)(\s|$)/i;
  const RE_REDEMPTION = /^(redemption|swp)(\s|$)/i;
  const RE_SWITCH_IN = /^(switch\s*in|stp\s*in|dividend\s*sweep\s*in|dividend\s*reinvestment|transfer\s*in)(\s|$)/i;
  const RE_SWITCH_OUT = /^(switch\s*out|stp\s*out|dividend\s*sweep\s*out|transfer\s*out)(\s|$)/i;
  const RE_DIVIDEND = /^dividend\s*payout(\s|$)/i;

  (allTransactions || []).forEach((t) => {
    const cat = classifyCategory(t.scheme_name || "");
    const b = buckets[cat] || buckets.Others;
    const desc = (t.description || t.transaction_desc || t.flag || "").trim();
    const treatment = (t.treatment || "").toLowerCase();
    const amt = Math.abs(parseFloat(t.amount) || 0);
    if (!amt) return;

    // Skip anything the RTAs flagged non-financial / failed / rejection.
    if (RE_EXCLUDE.test(desc)) return;
    if (["non financial", "failed transactions", "segregated"].some((k) => treatment.includes(k))) return;

    if (RE_SWITCH_IN.test(desc)) b.switchIn += amt;
    else if (RE_SWITCH_OUT.test(desc)) b.switchOut += amt;
    else if (RE_DIVIDEND.test(desc)) b.dividendPayout += amt;
    else if (RE_PURCHASE.test(desc)) b.investment += amt;
    else if (RE_REDEMPTION.test(desc)) b.redemption += amt;
    else {
      // Fallback (blank/unknown description) — use treatment as a
      // best-effort proxy. This preserves the row so totals don't drop.
      if (treatment.includes("add units")) b.investment += amt;
      else if (treatment.includes("minus units")) b.redemption += amt;
      else if (treatment.includes("dividend")) b.dividendPayout += amt;
    }
  });

  // XIRR cashflows come from filteredActiveUnits (active-lot ledger) —
  // per-lot cost basis as dated -outflows, terminal +market_value.
  // This matches MyPlanner's methodology (verified against Kanwaljit
  // Kohli's MyPlanner Cashflow Snapshot on 2026-09-08: both show 12.12%).
  (filteredActiveUnits || []).forEach((txn) => {
    const cat = classifyCategory(txn.scheme_name || "");
    const b = buckets[cat] || buckets.Others;
    const { cost, market, dateObj } = computeLotEconomics(txn);
    b.cost += cost;
    b.marketValue += market;
    b.unrealised += market - cost;
    if (cost > 0 && dateObj) b.cashflows.push({ date: dateObj, amount: -cost });
  });

  // (activeUnits bucketing moved into the same loop above.) If we have
  // activeUnitsCostMap, refine per-category market from it (more accurate NAV).
  const totalMarketFromMap = Object.values(activeUnitsCostMap || {}).reduce((s, v) => s + (v.market || 0), 0);
  const totalMarketFromLots = Object.values(buckets).reduce((s, b) => s + b.marketValue, 0);
  if (totalMarketFromMap > 0 && totalMarketFromLots > 0) {
    const scale = totalMarketFromMap / totalMarketFromLots;
    CATEGORIES.forEach((c) => { buckets[c].marketValue *= scale; buckets[c].unrealised = buckets[c].marketValue - buckets[c].cost; });
  }

  // Fallback if no lots (rare — new client): use holdings prop total in Others.
  if (totalMarketFromLots === 0 && totalMarketFromMap === 0) {
    buckets.Others.marketValue = holdingsFilteredByPattern.reduce((s, h) => s + (h.market_value || 0), 0);
  }

  // Compute derived rows per category.
  const perCat = CATEGORIES.map((c) => {
    const b = buckets[c];
    const cashInflow = b.investment + b.switchIn;
    const cashOutflow = b.switchOut + b.redemption + b.dividendPayout;
    const netInvestment = b.investment + b.switchIn - b.switchOut - b.redemption - b.dividendPayout;
    const netGain = b.marketValue - netInvestment;
    const realised = netGain - b.unrealised;
    // XIRR: Active-Lot method — per-lot cost basis as dated -outflows
    // + terminal +market_value. Matches MyPlanner (Kanwaljit Kohli
    // verified 2026-09-08: MP 12.12% = Kinntegra 12.12%).
    const xirrCfs = b.marketValue > 0 && b.cashflows.length > 0
      ? [...b.cashflows, { date: new Date(), amount: b.marketValue }]
      : [];
    const lifetimeXirr = xirrCfs.length > 0 ? computeXirr(xirrCfs) : 0;
    return { cat: c, ...b, cashInflow, cashOutflow, netInvestment, netGain, realised, lifetimeXirr };
  });

  // Totals column.
  const total = perCat.reduce((acc, p) => {
    acc.investment += p.investment; acc.switchIn += p.switchIn;
    acc.switchOut += p.switchOut; acc.redemption += p.redemption;
    acc.dividendPayout += p.dividendPayout;
    acc.marketValue += p.marketValue; acc.unrealised += p.unrealised;
    acc.cashflows = acc.cashflows.concat(p.cashflows);
    return acc;
  }, { investment: 0, switchIn: 0, switchOut: 0, redemption: 0, dividendPayout: 0, marketValue: 0, unrealised: 0, cashflows: [] });
  total.cashInflow = total.investment + total.switchIn;
  total.cashOutflow = total.switchOut + total.redemption + total.dividendPayout;
  total.netInvestment = total.investment + total.switchIn - total.switchOut - total.redemption - total.dividendPayout;
  total.netGain = total.marketValue - total.netInvestment;
  total.realised = total.netGain - total.unrealised;
  total.lifetimeXirr = total.marketValue > 0 && total.cashflows.length > 0
    ? computeXirr([...total.cashflows, { date: new Date(), amount: total.marketValue }])
    : 0;

  // Row definitions in the exact order of the reference screenshot.
  const rows = [
    { key: "cash-inflow", label: "Cash Inflow (a+b)", get: (b) => b.cashInflow, header: true, cls: "bg-emerald-50/60 font-semibold" },
    { key: "a", label: "a. Investment", get: (b) => b.investment },
    { key: "b", label: "b. Switch In", get: (b) => b.switchIn },
    { key: "cash-outflow", label: "Cash Outflow (c + d + e)", get: (b) => b.cashOutflow, header: true, cls: "bg-orange-50/60 font-semibold" },
    { key: "c", label: "c. Switch Out", get: (b) => b.switchOut },
    { key: "d", label: "d. Redemption", get: (b) => b.redemption },
    { key: "e", label: "e. Dividend Payout", get: (b) => b.dividendPayout },
    { key: "f", label: "f. Net Investment (a+b\u2212c\u2212d\u2212e)", get: (b) => b.netInvestment, cls: "font-semibold bg-gray-50" },
    { key: "g", label: "g. Market value", get: (b) => b.marketValue, cls: "font-semibold bg-gray-50" },
    { key: "h", label: "h. Net Gain (g\u2212f)", get: (b) => b.netGain, signed: true, cls: "font-semibold bg-gray-50" },
    { key: "realised", label: "Realised Gain / Loss", get: (b) => b.realised, signed: true, indent: true },
    { key: "unrealised", label: "Unrealised Gain / Loss", get: (b) => b.unrealised, signed: true, indent: true },
    { key: "i", label: "i. Lifetime XIRR (All Txn)", get: (b) => b.lifetimeXirr, pct: true, cls: "font-bold bg-teal-50" },
  ];

  return (
    <Card className="border-gray-200" data-testid="summary-cashflow-status-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-teal-600" />
              Cashflow Snapshot
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Since Inception</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold text-gray-700"></TableHead>
                {CATEGORIES.map((c) => (
                  <TableHead key={c} className="font-semibold text-right text-gray-700 uppercase text-xs tracking-wide">{c}</TableHead>
                ))}
                <TableHead className="font-semibold text-right text-gray-700 uppercase text-xs tracking-wide">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.key} className={r.cls || ""} data-testid={`cashflow-row-${r.key}`}>
                  <TableCell className={`text-sm text-gray-700 ${r.indent ? "pl-8" : ""} ${r.header || r.cls ? "font-semibold" : ""}`}>
                    {r.label}
                  </TableCell>
                  {perCat.map((p) => {
                    const v = r.get(p);
                    return (
                      <TableCell key={p.cat} className="text-right font-mono text-sm">
                        {r.pct
                          ? (v === 0 ? "-" : `${v >= 0 ? "" : "\u2212"}${Math.abs(v).toFixed(2)}%`)
                          : (Math.abs(v) < 1 ? "-" : (r.signed ? fmtSigned(v) : fmtIN(v)))}
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-right font-mono text-sm font-semibold">
                    {r.pct
                      ? (total.lifetimeXirr === 0 ? "-" : `${total.lifetimeXirr >= 0 ? "" : "\u2212"}${Math.abs(total.lifetimeXirr).toFixed(2)}%`)
                      : (Math.abs(r.get(total)) < 1 ? "-" : (r.signed ? fmtSigned(r.get(total)) : fmtIN(r.get(total))))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
